import { useEffect, useMemo, useState } from "react";
import {
  FileSignature, FileText, Check, Sparkles, Users, ShieldCheck, Loader2, Pencil,
  CheckCircle2, Circle, FileDown, Eye, Info, HelpCircle, History, Plus, X, BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, openSigned } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SignActions } from "@/components/SignActions";
import { SigningRequestBadges } from "@/components/SigningRequestBadges";
import { formatDate, formatDateTime } from "@/lib/format";
import { SignatoryFields, validateSignatories } from "./SignatoryFields";
import { AiConflictReport } from "./AiConflictReport";

const STATUS = {
  venter_signering: { label: "Venter på signering", cls: "border-amber-500/30 bg-amber-500/10 text-amber-600" },
  signert: { label: "Signert", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600" },
  gjennomfort: { label: "Signert", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600" },
  kansellert: { label: "Kansellert", cls: "border-border bg-muted text-muted-foreground" },
};

function initials(name = "") {
  return name.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

function AgreementCard({ a, onChanged, canEdit, onNewVersion, isOld }) {
  const meta = STATUS[a.status] || STATUS.kansellert;
  const signedCount = (a.parties || []).filter((p) => p.signed).length;
  return (
    <div className={isOld ? "rounded-xl border border-border/50 bg-muted/20 p-3" : "p-4"} data-testid={`sh-agreement-${a.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-1.5 font-medium">
            <FileSignature className="h-4 w-4 text-lime" /> {a.title}
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold text-muted-foreground">v{a.version || 1}</span>
          </p>
          <p className="text-xs text-muted-foreground">{(a.parties || []).length} parter · {formatDate(a.date)}</p>
        </div>
        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${meta.cls}`} data-testid={`sh-agreement-status-${a.id}`}>
          {meta.label}{a.status === "venter_signering" ? ` ${signedCount}/${(a.parties || []).length}` : ""}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs">
        {(a.parties || []).map((p, i) => (
          <span key={i} className="flex items-center gap-1.5">
            {p.signed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-muted-foreground/40" />}
            {p.name}: {p.signed ? `signert${p.method === "bankid" ? " (BankID)" : ""} ${formatDateTime(p.signed_at)}` : "avventer"}
          </span>
        ))}
      </div>

      {a.status === "venter_signering" ? <SigningRequestBadges entityType="agreement" entityId={a.id} /> : null}
      {a.kind === "aksjonaeravtale" && a.ai_conflict_check ? <AiConflictReport check={a.ai_conflict_check} testIdPrefix={`ai-conflict-${a.id}`} /> : null}

      <div className="mt-3 flex flex-wrap gap-2">
        {a.status === "venter_signering" ? <SignActions entityType="agreement" entityId={a.id} onChanged={onChanged} /> : null}
        <Button size="sm" variant="outline" className="gap-1.5" data-testid={`sh-agreement-pdf-${a.id}`}
          onClick={() => openSigned(`/agreements/${a.id}/pdf-link`).catch(() => toast.error("Kunne ikke åpne avtalen"))}>
          <FileDown className="h-4 w-4" /> Avtale (PDF)
        </Button>
        {a.bankid_signed_path ? (
          <Button size="sm" variant="outline" className="gap-1.5" data-testid={`sh-agreement-bankid-pdf-${a.id}`}
            onClick={() => openSigned(`/signing/agreement/${a.id}/signed-link`).catch(() => toast.error("Kunne ikke åpne det signerte dokumentet"))}>
            <ShieldCheck className="h-4 w-4 text-emerald-600" /> BankID-signert PDF
          </Button>
        ) : null}
        {canEdit && !isOld ? (
          <Button size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" data-testid={`sh-new-version-${a.id}`} onClick={() => onNewVersion(a)}>
            <History className="h-4 w-4" /> Lag ny versjon
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function ShareholderAgreements({ companyId, rows = [], agreements = [], onChanged, canEdit }) {
  const [templates, setTemplates] = useState([]);
  const [clauseDefs, setClauseDefs] = useState([]);
  const [template, setTemplate] = useState("standard");
  const [active, setActive] = useState({});
  const [texts, setTexts] = useState({});
  const [editing, setEditing] = useState({});
  const [helpOpen, setHelpOpen] = useState({});
  const [signers, setSigners] = useState(() => new Set(rows.map((r) => r.id)));
  const [signatories, setSignatories] = useState({});
  const [title, setTitle] = useState("");
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [explainerOpen, setExplainerOpen] = useState(false);
  const [supersedes, setSupersedes] = useState(null); // { id, title, version }
  const [showOld, setShowOld] = useState({});

  useEffect(() => {
    api.get("/agreement-templates").then((r) => {
      setTemplates(r.data.templates || []);
      setClauseDefs(r.data.clauses || []);
      setTexts(Object.fromEntries((r.data.clauses || []).map((c) => [c.id, c.text])));
      const std = (r.data.templates || []).find((t) => t.id === "standard") || (r.data.templates || [])[0];
      if (std) setActive(Object.fromEntries((r.data.clauses || []).map((c) => [c.id, std.clauses.includes(c.id)])));
    }).catch(() => toast.error("Kunne ikke laste maler"));
  }, []);

  useEffect(() => { setSigners(new Set(rows.map((r) => r.id))); }, [rows]);

  const pickTemplate = (id) => {
    setTemplate(id);
    const t = templates.find((x) => x.id === id);
    if (t) setActive(Object.fromEntries(clauseDefs.map((c) => [c.id, t.clauses.includes(c.id)])));
  };

  const chosen = useMemo(() => clauseDefs.filter((c) => active[c.id]), [clauseDefs, active]);
  const shAgreements = useMemo(() => agreements.filter((a) => a.kind === "aksjonaeravtale"), [agreements]);

  const versionGroups = useMemo(() => {
    const byRoot = {};
    shAgreements.forEach((a) => {
      const root = a.root_id || a.id;
      (byRoot[root] ||= []).push(a);
    });
    return Object.values(byRoot)
      .map((list) => list.sort((x, y) => (y.version || 1) - (x.version || 1)))
      .sort((A, B) => new Date(B[0].created_at || 0) - new Date(A[0].created_at || 0));
  }, [shAgreements]);

  const toggleSigner = (id) => setSigners((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const startNewVersion = (a) => {
    setSupersedes({ id: a.id, title: a.title, version: a.version || 1 });
    if (a.template_id) setTemplate(a.template_id);
    const ids = new Set((a.clauses || []).map((c) => c.id));
    setActive(Object.fromEntries(clauseDefs.map((c) => [c.id, ids.has(c.id)])));
    setTexts((t) => ({ ...t, ...Object.fromEntries((a.clauses || []).map((c) => [c.id, c.text])) }));
    const partyIds = (a.parties || []).map((p) => p.shareholder_id).filter((id) => rows.some((r) => r.id === id));
    setSigners(new Set(partyIds.length ? partyIds : rows.map((r) => r.id)));
    setTitle(a.title || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info(`Forhåndsutfylt fra «${a.title}» (v${a.version || 1}). Endre og generer ny versjon.`);
  };

  const buildBody = () => {
    const clauses = chosen.map((c) => ({ id: c.id, title: c.title, text: texts[c.id] ?? c.text }));
    const allSelected = signers.size === rows.length;
    const sigs = {};
    rows.forEach((r) => {
      if (signers.has(r.id) && r.holder_type === "selskap") {
        sigs[r.id] = (signatories[r.id] || []).map((s) => ({ name: s.name || "", id_number: s.id_number || "", email: s.email || "" }));
      }
    });
    return {
      template_id: template, title: title.trim(), clauses,
      signer_shareholder_ids: allSelected ? null : Array.from(signers),
      signatories: Object.keys(sigs).length ? sigs : undefined,
      supersedes_id: supersedes?.id || null,
    };
  };

  const firstSignatoryError = () => {
    for (const r of rows) {
      if (signers.has(r.id) && r.holder_type === "selskap") {
        const err = validateSignatories(signatories[r.id], r.name);
        if (err) return err;
      }
    }
    return "";
  };

  const preview = async () => {
    if (chosen.length === 0) { toast.error("Velg minst én klausul"); return; }
    setPreviewing(true);
    try {
      const res = await api.post(`/companies/${companyId}/shareholder-agreements/preview-pdf`, buildBody(), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Kunne ikke lage forhåndsvisning");
    } finally {
      setPreviewing(false);
    }
  };

  const generate = async () => {
    if (chosen.length === 0) { toast.error("Velg minst én klausul"); return; }
    if (signers.size === 0) { toast.error("Velg minst én aksjonær som part"); return; }
    const sigErr = firstSignatoryError();
    if (sigErr) {
      toast.error(sigErr);
      return;
    }
    setGenerating(true);
    try {
      await api.post(`/companies/${companyId}/shareholder-agreements`, buildBody());
      toast.success(supersedes ? "Ny versjon opprettet – klar for BankID-signering" : "Aksjonæravtale opprettet – klar for BankID-signering");
      setTitle("");
      setSupersedes(null);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-10">
      {canEdit ? (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* LEFT — builder */}
          <div className="flex flex-col gap-8 lg:col-span-8">
            {supersedes ? (
              <div className="flex items-center justify-between gap-3 rounded-2xl border border-lime/40 bg-lime/[0.06] px-4 py-3" data-testid="sh-new-version-banner">
                <span className="flex items-center gap-2 text-sm">
                  <History className="h-4 w-4 text-lime" /> Lager <b>ny versjon</b> av «{supersedes.title}» (v{supersedes.version} → v{supersedes.version + 1})
                </span>
                <button onClick={() => setSupersedes(null)} className="text-muted-foreground hover:text-foreground" data-testid="sh-cancel-new-version"><X className="h-4 w-4" /></button>
              </div>
            ) : null}

            <section>
              <div className="mb-3 flex items-center gap-2">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-lime text-xs font-bold text-lime-foreground">1</span>
                <h2 className="font-headings text-lg font-bold tracking-tight">Velg mal</h2>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {templates.map((t) => {
                  const sel = template === t.id;
                  return (
                    <button key={t.id} onClick={() => pickTemplate(t.id)} data-testid={`sh-template-${t.id}`}
                      className={`relative rounded-2xl border p-5 text-left transition-colors ${sel ? "border-lime bg-lime/[0.06] ring-1 ring-lime" : "border-border/60 bg-card hover:border-border"}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className={`grid h-9 w-9 place-items-center rounded-xl ${sel ? "bg-lime text-lime-foreground" : "bg-muted text-muted-foreground"}`}>
                          <FileText className="h-4.5 w-4.5" />
                        </div>
                        {t.tag ? <span className="rounded-full bg-lime/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-lime">{t.tag}</span>
                          : sel ? <span className="grid h-5 w-5 place-items-center rounded-full bg-lime text-lime-foreground"><Check className="h-3 w-3" /></span> : null}
                      </div>
                      <p className="mt-3 font-semibold">{t.name}</p>
                      <p className="mt-0.5 text-sm text-muted-foreground">{t.desc}</p>
                      <p className="mt-2 text-xs text-muted-foreground">{t.clauses.length} klausuler</p>
                    </button>
                  );
                })}
              </div>
            </section>

            <section>
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-lime text-xs font-bold text-lime-foreground">2</span>
                  <h2 className="font-headings text-lg font-bold tracking-tight">Velg klausuler</h2>
                </div>
                <span className="text-sm text-muted-foreground">{chosen.length} av {clauseDefs.length} valgt</span>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {clauseDefs.map((c) => {
                  const on = !!active[c.id];
                  const info = !!helpOpen[c.id];
                  return (
                    <div key={c.id} className={`rounded-2xl border p-4 transition-colors ${on ? "border-lime/40 bg-lime/[0.04]" : "border-border/60 bg-card"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <button className="min-w-0 flex-1 text-left" onClick={() => setHelpOpen((h) => ({ ...h, [c.id]: !h[c.id] }))} data-testid={`sh-clause-help-${c.id}`}>
                          <p className="flex items-center gap-1.5 font-medium">{c.title} <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/70" /></p>
                          <p className="mt-0.5 text-xs text-muted-foreground">{c.desc}</p>
                        </button>
                        <Switch checked={on} onCheckedChange={() => setActive((a) => ({ ...a, [c.id]: !a[c.id] }))} data-testid={`sh-clause-${c.id}`} />
                      </div>
                      {info ? (
                        <div className="mt-2 rounded-xl bg-muted/40 p-3 text-xs" data-testid={`sh-clause-info-${c.id}`}>
                          <p className="flex items-start gap-1.5"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-lime" /> <span>{c.help}</span></p>
                          {c.example ? <p className="mt-1.5 text-muted-foreground"><b className="text-foreground/70">Eksempel:</b> {c.example}</p> : null}
                        </div>
                      ) : null}
                      {on ? (
                        <div className="mt-2">
                          <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
                            data-testid={`sh-clause-edit-${c.id}`} onClick={() => setEditing((e) => ({ ...e, [c.id]: !e[c.id] }))}>
                            <Pencil className="h-3 w-3" /> {editing[c.id] ? "Skjul tekst" : "Rediger tekst"}
                          </Button>
                          {editing[c.id] ? (
                            <Textarea className="mt-2 min-h-[110px] text-sm" value={texts[c.id] ?? ""} data-testid={`sh-clause-text-${c.id}`}
                              onChange={(ev) => setTexts((t) => ({ ...t, [c.id]: ev.target.value }))} />
                          ) : (
                            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{texts[c.id]}</p>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          {/* RIGHT — live preview + actions */}
          <div className="lg:col-span-4">
            <div className="sticky top-6 flex flex-col gap-4">
              <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  <Sparkles className="h-3.5 w-3.5 text-lime" /> Forhåndsvisning
                </div>
                <p className="mt-2 font-headings text-lg font-bold tracking-tight">{templates.find((t) => t.id === template)?.name || "Aksjonæravtale"}</p>

                <div className="mt-4">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5" /> Inkluderte punkter</p>
                  <div className="space-y-1.5">
                    {chosen.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Ingen klausuler valgt ennå.</p>
                    ) : chosen.map((c, i) => (
                      <div key={c.id} className="flex items-center gap-2 text-sm">
                        <span className="grid h-4 w-4 place-items-center rounded-full bg-lime/20 text-lime"><Check className="h-2.5 w-2.5" /></span>
                        <span className="text-muted-foreground"><span className="mr-1 tabular-nums text-foreground/50">§{i + 1}</span>{c.title}</span>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="mt-3 w-full gap-1.5 rounded-full" onClick={() => setExplainerOpen(true)} disabled={chosen.length === 0} data-testid="sh-explainer-btn">
                    <BookOpen className="h-4 w-4" /> Forhåndsvis for aksjonærer
                  </Button>
                </div>

                <div className="mt-5 border-t border-border/60 pt-4">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Users className="h-3.5 w-3.5" /> Parter som signerer</p>
                  {rows.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Ingen aksjonærer i aksjeeierboken ennå.</p>
                  ) : (
                    <div className="space-y-1">
                      {rows.map((r) => (
                        <div key={r.id}>
                          <label className="flex cursor-pointer items-center justify-between gap-2 rounded-lg px-1.5 py-1.5 text-sm hover:bg-muted/50" data-testid={`sh-signer-${r.id}`}>
                            <span className="flex items-center gap-2">
                              <Checkbox checked={signers.has(r.id)} onCheckedChange={() => toggleSigner(r.id)} />
                              <span className="grid h-6 w-6 place-items-center rounded-full bg-muted text-[10px] font-bold text-muted-foreground">{initials(r.name)}</span>
                              {r.name}
                            </span>
                            <span className="text-muted-foreground tabular-nums">{r.ownership_pct}%</span>
                          </label>
                          {signers.has(r.id) && r.holder_type === "selskap" ? (
                            <div className="ml-6 mb-1.5">
                              <SignatoryFields companyName={r.name} testIdPrefix={`sh-signatory-${r.id}`}
                                orgNumber={r.id_number}
                                value={signatories[r.id]}
                                onChange={(v) => setSignatories((prev) => ({ ...prev, [r.id]: v }))} />
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
                <Input placeholder="Tittel (valgfritt) – f.eks. «Aksjonæravtale 2026»" value={title}
                  onChange={(e) => setTitle(e.target.value)} data-testid="sh-title-input" />
                <Button variant="outline" className="mt-3 w-full gap-2 rounded-full" onClick={preview} disabled={previewing} data-testid="sh-preview-btn">
                  {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Forhåndsvis avtale (PDF)
                </Button>
                <Button className="mt-2 w-full gap-2 rounded-full bg-lime font-semibold text-lime-foreground hover:bg-lime/90"
                  onClick={generate} disabled={generating} data-testid="sh-generate-btn">
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : supersedes ? <History className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                  {supersedes ? "Generer ny versjon" : "Generer avtale"}
                </Button>
                <p className="mt-3 text-center text-xs text-muted-foreground">Alle valgte parter signerer digitalt med BankID</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p className="rounded-xl border border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground">
          Kun styreleder, nestleder, daglig leder eller admin kan opprette aksjonæravtaler. Du kan se og åpne inngåtte avtaler under.
        </p>
      )}

      {/* Existing agreements grouped by version */}
      <section>
        <h2 className="mb-3 font-headings text-lg font-bold tracking-tight">Aksjonæravtaler</h2>
        {versionGroups.length === 0 ? (
          <p className="rounded-xl border border-border/60 bg-muted/30 p-6 text-center text-sm text-muted-foreground" data-testid="sh-agreements-empty">
            Ingen aksjonæravtaler ennå.
          </p>
        ) : (
          <div className="space-y-4">
            {versionGroups.map((group) => {
              const [latest, ...older] = group;
              const root = latest.root_id || latest.id;
              const open = !!showOld[root];
              return (
                <div key={root} className="overflow-hidden rounded-2xl border border-border/60 bg-card">
                  <AgreementCard a={latest} onChanged={onChanged} canEdit={canEdit} onNewVersion={startNewVersion} />
                  {older.length ? (
                    <div className="border-t border-border/60 bg-muted/20 px-4 py-3">
                      <button className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
                        onClick={() => setShowOld((s) => ({ ...s, [root]: !s[root] }))} data-testid={`sh-history-toggle-${root}`}>
                        <History className="h-3.5 w-3.5" /> {open ? "Skjul" : "Vis"} tidligere versjoner ({older.length})
                      </button>
                      {open ? (
                        <div className="mt-3 space-y-3" data-testid={`sh-history-${root}`}>
                          {older.map((o) => <AgreementCard key={o.id} a={o} onChanged={onChanged} canEdit={canEdit} isOld />)}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Shareholder-friendly explainer */}
      <Dialog open={explainerOpen} onOpenChange={setExplainerOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="sh-explainer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BookOpen className="h-5 w-5 text-lime" /> Forklaring for aksjonærer</DialogTitle>
            <DialogDescription>
              En lettlest oversikt over hva hvert punkt i avtalen betyr, med eksempler. Dette er en veiledning – selve avtaleteksten er den juridisk bindende.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {chosen.map((c, i) => (
              <div key={c.id} className="rounded-2xl border border-border/60 bg-card p-4">
                <p className="font-semibold"><span className="mr-1.5 tabular-nums text-muted-foreground">§{i + 1}</span>{c.title}</p>
                <p className="mt-1.5 flex items-start gap-1.5 text-sm"><Info className="mt-0.5 h-4 w-4 shrink-0 text-lime" /> <span>{c.help}</span></p>
                {c.example ? (
                  <p className="mt-2 rounded-xl bg-muted/40 p-3 text-sm text-muted-foreground"><b className="text-foreground/70">Eksempel: </b>{c.example}</p>
                ) : null}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
